import React, { useState } from "react";
import { useAppDispatch } from "../../../State/Store";
import {
  Button,
  CircularProgress,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { sellerLogin } from "../../../State/seller/sellerAuthSlice";
import { useNavigate } from "react-router-dom";

const SellerLoginForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .required("Email or Username is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      const formattedPayload = {
        email: values.email.trim(),
        password: values.password,
      };

      await dispatch(sellerLogin({ loginRequest: formattedPayload, navigate }));
      setLoading(false);
    },
  });

  return (
    <Box className="w-full space-y-6">
      <div>
        <Typography variant="h4" className="font-extrabold text-slate-900 tracking-tight text-2xl sm:text-3xl">
          Sign In
        </Typography>
        <Typography variant="body2" className="text-slate-500 mt-1">
          Enter your credentials to access your seller store dashboard.
        </Typography>
      </div>

      <form onSubmit={formik.handleSubmit} className="space-y-4">
        <TextField
          fullWidth
          id="email"
          name="email"
          label="Email Address"
          placeholder="seller@example.com"
          variant="outlined"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email className="text-slate-400" fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
            },
          }}
        />

        <TextField
          fullWidth
          id="password"
          name="password"
          type={showPassword ? "text" : "password"}
          label="Password"
          placeholder="••••••••"
          variant="outlined"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock className="text-slate-400" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
            },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-3.5 rounded-xl shadow-md normal-case text-base"
          sx={{
            py: "12px",
            fontSize: "1rem",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: "12px",
            backgroundColor: "#0d9488",
            "&:hover": {
              backgroundColor: "#0f766e",
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
        </Button>
      </form>
    </Box>
  );
};

export default SellerLoginForm;